import { ApolloClient, ObservableQuery } from '@apollo/client'
import {Component, Host, h, Event, State, Prop, EventEmitter, Method} from '@stencil/core'
import { GetAuthStatusDocument, GetAuthStatusQuery, GetAuthStatusQueryVariables } from '../../graphql/generated'
import { DropdownItemSelected, DropdownMenuItem } from '../../utils'
import {Components} from "../../components";
import SnapSession = Components.SnapSession;

@Component({
  tag: 'snap-header-wrapper',
  styleUrl: 'snap-header-wrapper.scss',
  scoped: true,
})
export class SnapHeaderWrapper {
  observable: ObservableQuery<GetAuthStatusQuery, GetAuthStatusQueryVariables>
  client: ApolloClient<any>
  private _subscription: ZenObservable.Subscription
  private snapSessionRef: SnapSession

  /**
   * When set to false org admin icon will not show
   */
  @Prop() showOrgAdmin = false

  /**
   * When set to false messaging icon will not show
  */
  @Prop() showMessaging = true

  /**
   * When set to false app switcher and the divider bar will not show
   */
  @Prop() showAppSwitcher = true
  /**
   * Prevent login buttons from showing
   */
  @Prop() showLoginButtons = true

  /**
   * See snap session for documentation
   */
  @Prop() redirectOnSignin?: string
  /**
   * See snap session for documentation
   */
  @Prop() redirectOnSignout?: string
  /**
   * See snap-session for documentation
   */
  @Prop() menuItems: DropdownMenuItem[] = []

  /**
   * See snap-app-switcher for documentation
   */
  @Prop() showAppSwitherBadge = false

  @State() data: GetAuthStatusQuery

  /**
   * This event is published when an item in the snap-session dropdown has been selected. It is published with that items info.
   */
  @Event({ eventName: 'snap-header-session-item-selected' }) snapHeaderSessionItemSelected: EventEmitter<DropdownItemSelected>

  @Method()
  async updateAuth() {
    this.snapSessionRef.updateAuth()
  }

  connectedCallback() {
    if (!this.client) {
      this.client = window.__SNAP_APOLLO_CLIENT__
    }
    if (!!this.client && !this._subscription) {
      this.watchForAuth()
    }
  }

  disconnectedCallback() {
    this._subscription.unsubscribe()
  }

  watchForAuth() {
    this.observable = this.client.watchQuery({ query: GetAuthStatusDocument })
    this._subscription = this.observable.subscribe(
      res => {
        this.data = res.data
        if (!!res.error || !!res.errors) {
          this.handleError(res.error)
        }
        if (res.errors) {
          this.handleError(res.errors)
        }
      },
      error => {
        this.handleError(error)
      }
    )
  }

  handleError(error: any) {
    console.error(error)
    this.data = null
  }

  handleMenuSelection = (ev: CustomEvent<DropdownItemSelected>) => {
    this.snapHeaderSessionItemSelected.emit(ev.detail)
  }

  render() {
    const authReady = !!this.data && !!this.data.apiReady
    return (
      <Host>

        {!!(authReady && this.showAppSwitcher) && (
          <div class="app-switcher-container">
            <snap-app-switcher badge={this.showAppSwitherBadge} />
          </div>
        )}

        {!!(authReady && this.showMessaging) && (
          <div class="messaging-container">
            <snap-messaging-icon />
          </div>
        )}

        {!!(authReady && this.showOrgAdmin) && (
          <div class="org-admin-container">
            <snap-org-admin />
          </div>
        )}

        {!!(authReady && (this.showAppSwitcher)) && (
          <div class="divider-bar" />
        )}

        {!!(this.showLoginButtons || authReady) &&
          <snap-session
            inGlobalHeader={true}
            redirectOnSignin={this.redirectOnSignin}
            redirectOnSignout={this.redirectOnSignout}
            menuItems={this.menuItems}
            respectAuthStatus={true}
            authReady={authReady}
            onSnap-session-menu-item-selected={this.handleMenuSelection}
            ref={el => (this.snapSessionRef = el)}
          />}
      </Host>
    )
  }
}
